import React, { Component } from 'react'
import { object } from 'prop-types'
import { createComponent } from '@ycos/fela'

/*
This should only return false if the browser doesn't support intersection observer
*/
const isTestEnvironment = process.env.NODE_ENV === 'test'
const doesWindowSupportIntersectionObserver = () =>
  'IntersectionObserver' in window && 'IntersectionObserverEntry' in window && 'intersectionRatio' in window.IntersectionObserverEntry.prototype

const intersectionObserverSupport = typeof window !== 'undefined' ? isTestEnvironment || doesWindowSupportIntersectionObserver() : false

const LazyLoadedPictureWrapper = createComponent(
  'Picture',
  ({ lazy, position, isVisible }) => {
    return lazy
      ? {
        position: position || 'absolute',
        left: 0,
        width: '100%',
        height: '100%',
        opacity: isVisible ? 1 : 0,
        wordBreak: 'break-word',
        transition: 'opacity 0.2s ease',
        'screen-medium': {
          height: '100%'
        }
      }
      : {
        position: position || 'absolute',
        left: 0,
        width: '100%',
        height: '100%',
        'screen-medium': {
          height: '100%'
        }
      }
  },
  'picture'
)

class LazyLoadedPicture extends Component {
  constructor() {
    super()

    this.pictureRef = React.createRef()

    this.state = {
      isVisible: false
    }
  }

  componentDidMount() {
    const lazyloadingSupport = 'loading' in HTMLImageElement.prototype
    const { lazy, intersectionObserverOptions } = this.props
    if (lazy) {
      if (intersectionObserverSupport && !lazyloadingSupport) {
        const target = this.pictureRef.current
        this.observer = new IntersectionObserver(this.intersectionObserverCallback, intersectionObserverOptions)
        this.observer.observe(target)
      } else {
        this.updateImageSrc()
        this.setState({
          isVisible: true
        })
      }
    }
  }

  componentDidUpdate() {
    if (this.state.isVisible) {
      this.updateImageSrc()
    }
  }

  componentWillUnmount() {
    const target = this.pictureRef.current

    // When the seagulls flock, you gotta unobserve
    if (this.observer) {
      this.observer.unobserve(target)
    }
  }

  intersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      const { isIntersecting } = entry

      if (isIntersecting) {
        this.updateImageSrc()
        this.observer.unobserve(entry.target)
        this.setState({
          isVisible: true
        })
      }
    })
  }

  updateImageSrc() {
    // in some rare cases the pictureRef is null
    if (!this.pictureRef?.current) {
      return
    }

    const img = this.pictureRef.current.querySelector('img')
    const sources = this.pictureRef.current.querySelectorAll('source')
    const imgSrc = img.getAttribute('src')
    const dataImgSrc = img.getAttribute('data-src')

    // Don't be a silly little goose and re-set the image source.
    if (!dataImgSrc || imgSrc === dataImgSrc) {
      return
    }

    const dataImgSrcSet = img.getAttribute('data-src-set')

    sources.forEach((source) => {
      const dataSourceSrcSet = source.getAttribute('data-src-set')
      source.setAttribute('srcSet', dataSourceSrcSet)
    })

    img.setAttribute('src', dataImgSrc)
    img.setAttribute('srcSet', dataImgSrcSet)
  }

  render() {
    const { isVisible } = this.state
    const { lazy, position } = this.props

    return (
      <LazyLoadedPictureWrapper lazy={lazy} position={position} isVisible={isVisible} innerRef={this.pictureRef}>
        {this.props.children}
      </LazyLoadedPictureWrapper>
    )
  }
}

export default LazyLoadedPicture

LazyLoadedPicture.propTypes = {
  intersectionObserverOptions: object
}

LazyLoadedPicture.defaultProps = {
  intersectionObserverOptions: {
    rootMargin: '0px 0px 350px 0px'
  }
}
